import { useState, useEffect } from 'react';
import type { HtmlEmbedFormData, EmbedTab } from './types';
import {
  DEFAULT_FORM_DATA,
  DEFAULT_CUSTOM_EMBED,
  DEFAULT_VIDEO_EMBED,
  DEFAULT_IFRAME_EMBED,
  DEFAULT_CONTAINER_STYLE,
} from './types';

interface UseHtmlEmbedFormReturn {
  formData: HtmlEmbedFormData;
  activeTab: EmbedTab;
  setActiveTab: (tab: EmbedTab) => void;
  handleInputChange: (section: string, field: string, value: any) => void;
  handleTopLevelChange: (field: string, value: any) => void;
  handleVideoUrlPaste: (url: string) => void;
}

// Extract YouTube video ID from URL
function extractYouTubeId(url: string): string {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length === 11 ? match[7] : url;
}

// Extract Vimeo video ID from URL
function extractVimeoId(url: string): string {
  const regExp = /vimeo.*\/(\d+)/i;
  const match = url.match(regExp);
  return match ? match[1] : url;
}

export function useHtmlEmbedForm(isOpen: boolean, initialData?: any): UseHtmlEmbedFormReturn {
  const [activeTab, setActiveTab] = useState<EmbedTab>('custom');
  const [formData, setFormData] = useState<HtmlEmbedFormData>(DEFAULT_FORM_DATA);

  // Update form data when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        embedType: initialData.embedType || 'custom',
        custom: initialData.custom || DEFAULT_CUSTOM_EMBED,
        video: initialData.video || DEFAULT_VIDEO_EMBED,
        iframe: initialData.iframe || DEFAULT_IFRAME_EMBED,
        responsive: initialData.responsive !== undefined ? initialData.responsive : true,
        lazyLoad: initialData.lazyLoad || false,
        containerStyle: initialData.containerStyle || DEFAULT_CONTAINER_STYLE,
      });

      // Set active tab based on embed type
      if (initialData.embedType === 'custom') {
        setActiveTab('custom');
      } else if (initialData.embedType === 'video') {
        setActiveTab('video');
      } else if (initialData.embedType === 'iframe') {
        setActiveTab('iframe');
      }
    }
  }, [isOpen, initialData]);

  const handleInputChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section as keyof HtmlEmbedFormData] as Record<string, any>),
        [field]: value,
      },
    }));
  };

  const handleTopLevelChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVideoUrlPaste = (url: string) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = extractYouTubeId(url);
      handleInputChange('video', 'videoId', videoId);
      handleInputChange('video', 'platform', 'youtube');
    } else if (url.includes('vimeo.com')) {
      const videoId = extractVimeoId(url);
      handleInputChange('video', 'videoId', videoId);
      handleInputChange('video', 'platform', 'vimeo');
    }
  };

  return {
    formData,
    activeTab,
    setActiveTab,
    handleInputChange,
    handleTopLevelChange,
    handleVideoUrlPaste,
  };
}
